import PropTypes from 'prop-types';
import QRCode from '../../components/QRCode';
import { useShowcaseText } from '../../i18n/useShowcaseText';
import { CurlyArrow } from './CurlyArrow';
import './ShowroomQrCodes.css';

/**
 * ShowroomQrCodes — код, который клеят на лобовое стекло (#1337, план #1330).
 *
 * ## Код стоит РЯДОМ со снимком, а не на нём
 *
 * Раньше он был вкомпонован в стекло машины по замеренной матрице плоскости
 * (наклон крыши −1.6°, стойка −39°). Выглядело достоверно, но читалось плохо:
 * на тонированном стекле код мелкий и тёмный, отсканировать его со страницы
 * нельзя, а понять, что это именно наклейка, а не артефакт снимка, — трудно.
 *
 * Теперь код лежит отдельно и крупно, а куда его клеят — показывает стрелка
 * в сторону стекла. Сценарий объясняется так же, зато код настоящий и
 * сканируется прямо с экрана.
 *
 * Сценарий из синка #237: шоурум закрыт, нерабочее время — покупатель
 * сканирует код на машине и смотрит сток сам.
 *
 * ## Снимок и карточка, а не три плитки
 *
 * Снимок идёт во всю ширину раздела и выровнен по его тексту, а код с
 * объяснением лежит белой карточкой, ЗАХОДЯЩЕЙ на снимок справа. Тремя
 * равными плитками это читалось перечислением, а карточка поверх кадра
 * говорит «вот эта штука — на этой машине» без слов.
 *
 * ## Код настоящий, а не картинка
 *
 * Рендерим тем же атомом `QRCode`, что и печатные листовки. Показывать
 * нарисованный QR на демо, где дилер наверняка наведёт телефон, значит
 * проиграть показ на ровном месте. Поэтому у кода белое поле вокруг: без
 * тихой зоны сканеры его не берут.
 *
 * Лежит в `pages/shared/`, а не в `components/`: `bootstrap.jsx` инлайнит в
 * Shadow DOM дилера глоб `components/**\/*.css` целиком, и блок витрины иначе
 * уехал бы в бандл каждому клиенту.
 */
/*
 * ## Один блок на две сцены (#1417)
 *
 * Тем же приёмом собрана и обложка страницы: общий план салона с кодом,
 * врезанным в нижний левый угол, и стрелкой от двери. Отличаются сцены
 * снимком, углом врезки и рисунком стрелки — всё это пропсы, а доли кадра
 * для каждой сцены лежат модификатором в CSS (`.showroom-qr--bottom-start`).
 */
export const ShowroomQrCodes = ({
  carUrl,
  photo = '/assets/showroom-windscreen.webp',
  corner = 'top-end',
  arrowShape = 'loop',
  className = '',
}) => {
  const showcaseText = useShowcaseText();
  return (
    <figure
      className={`showroom-qr showroom-qr--${corner}${className ? ` ${className}` : ''}`}
    >
      <span className="showroom-qr__shot">
        <img
          className="showroom-qr__photo"
          src={photo}
          alt=""
          loading="lazy"
          width="1600"
          height="900"
        />

        {/* Код ВРЕЗАН в угол кадра, а не лежит карточкой поверх него: снимок
              обходит его обратным скруглением, то есть код выглядит вырезанным
              из кадра, а не приклеенным сверху. Тот же приём, что у врезки
              адресов в карте. */}
        <span className="showroom-qr__card">
          <span className="showroom-qr__code">
            {/* Без своей белой подложки (#1417): врезка цвета полотна, и белый
                квадрат кода на ней читался заплаткой. Модули чёрные на
                `--theme-page-bg` — контраст для сканера тот же. */}
            <QRCode
              url={carUrl}
              size={256}
              href={carUrl}
              bgColor="transparent"
              ariaLabel={showcaseText('showcase.a11y.openQr', 'Open landing page encoded in QR')}
            />
          </span>
        </span>

        {/* Стрелка от кода к стеклу — она и есть объяснение «клеится сюда».
              Внутри кадра, а не поверх всей фигуры: и код, и стекло лежат в
              нём, и доли кадра одни и те же на любой ширине. */}
        <CurlyArrow shape={arrowShape} startDot className="showroom-qr__arrow" />
      </span>
    </figure>
  );
};

ShowroomQrCodes.propTypes = {
  /** Абсолютный URL, который открывает код (лендинг машины или весь сток). */
  carUrl: PropTypes.string.isRequired,
  /** Снимок сцены. */
  photo: PropTypes.string,
  /** Угол кадра, в который врезан код. */
  corner: PropTypes.oneOf(['top-end', 'bottom-start']),
  /** Рисунок стрелки от места наклейки к коду. */
  arrowShape: PropTypes.oneOf(['loop', 'wave', 'arc', 'sweep', 'dive']),
  /** Модификатор сцены: доли кадра для стрелки и пропорция снимка. */
  className: PropTypes.string,
};

export default ShowroomQrCodes;
